import { type IamApi } from '@/features/iam/api/iam-api'
import { memoizeAsync } from '@/utils/memoize-decorator'
import { TeamRole, type TeamRoleProps } from './TeamRole'
import { collection } from '@/infra/collection'
import { Id } from '@/infra/id'
import { mapTeamRoleResourceToProps } from './team-role-mappers'

export interface ListTeamRolesQuery {
  workspaceId: Id
}

export type PrimeTeamRoleFn = (props: Partial<TeamRoleProps>) => TeamRole

export class TeamRoleStore {
  readonly teamRoles = collection({
    getDataId: (data) => data.id,
    getModelId: (model) => model.id,
    create: () => new TeamRole(),
  })

  constructor(private readonly _api: IamApi) {}

  /**
   * Gets the local team roles of a workspace.
   */
  forWorkspace(workspaceId: Id) {
    const id = Id.assert(workspaceId)
    return this.teamRoles.filter((x) => x.workspaceId === id)
  }

  /**
   * Lists the team roles of a workspace.
   */
  async listTeamRoles(
    query: ListTeamRolesQuery,
    signal?: AbortSignal,
  ): Promise<TeamRole[]> {
    const response = await this._api.teamRoles.list(
      { workspace_id: Id.assert(query.workspaceId) },
      signal,
    )
    return this.teamRoles.set(response.map(mapTeamRoleResourceToProps))
  }

  /**
   * Lists the team roles of a workspace, once per workspace.
   */
  @memoizeAsync()
  async listWorkspaceTeamRoles(
    workspaceId: Id,
    signal?: AbortSignal,
  ): Promise<TeamRole[]> {
    return this.listTeamRoles({ workspaceId: Id.assert(workspaceId) }, signal)
  }
}
